import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'

import { DriveFileMove, PersonOff, Restore } from '@mui/icons-material'
import { Box, Card, Link, Skeleton, Stack, TableContainer, TextField, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { isFetchBaseQueryError } from '../../../../../helpers/isFetchBaseQueryError'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import {
  useGetInternalOrganizationsQuery,
  useGetInternalOrphanedUsersQuery,
  useGetInternalUsersQuery,
  useRemoveInternalOrganizationUserMutation,
  useSetInternalOrganizationUserActiveMutation,
} from '../../../../api/organizations'
import { EditUserDialog } from '../../../../components/EditUserDialog/EditUserDialog'
import { OrphanedUsersDialog } from '../../../../components/OrphanedUsersDialog/OrphanedUsersDialog'
import { QueryStateBoundary } from '../../../../components/QueryStateBoundary/QueryStateBoundary'
import { RowActions } from '../../../../components/RowActions/RowActions'
import { UserStatusIcon, userStatusOrder } from '../../../../components/UserStatusIcon/UserStatusIcon'
import { getValidationErrorMessage } from '../../../../helpers/apiValidationError'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import { getUserDisplayName, getUserStatus } from '../../../organizations/components/helpers/organizationUserHelpers'
import { InternalOrganization, InternalUser } from '../../../organizations/types/Organizations'
import { MoveUserDialog } from '../MoveUserDialog/MoveUserDialog'
import { filterUsersByEmail, getOrganizationLabel, getUsersWithOrganization } from '../helpers/userListHelpers'

type CustomProps = {
  organizationsById: Map<string, InternalOrganization>
  onEdit: (user: InternalUser) => void
  onMove: (user: InternalUser) => void
  onToggleActive: (user: InternalUser) => void
  onRemove: (user: InternalUser) => void
}

type PendingAction = { kind: 'toggle' | 'remove'; user: InternalUser }

const nameOrDash = (value: string | null) => value || '---'

/** Every user with an organization: e-mail search, edit, move, enable or disable, and remove from the organization. */
export const UsersList: FC = () => {
  const { t } = useTranslation()
  const { notify, run } = useMutationFeedback()
  const [searchParams, setSearchParams] = useSearchParams()
  const usersQuery = useGetInternalUsersQuery()
  const organizationsQuery = useGetInternalOrganizationsQuery()
  const orphansQuery = useGetInternalOrphanedUsersQuery()
  const [setActive] = useSetInternalOrganizationUserActiveMutation()
  const [removeUser] = useRemoveInternalOrganizationUserMutation()
  const [confirm, setConfirm] = useState<ConfirmDialogData<PendingAction>>()
  const [editing, setEditing] = useState<{ open: boolean; user?: InternalUser; organization?: InternalOrganization }>({ open: false })
  const [moving, setMoving] = useState<{ open: boolean; user?: InternalUser }>({ open: false })

  const search = searchParams.get('search') ?? ''
  const orphansOpen = searchParams.get('orphans') === '1'
  const setParam = useCallback(
    (name: string, value?: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current)
          if (value) next.set(name, value)
          else next.delete(name)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const organizations = useMemo(() => organizationsQuery.data ?? [], [organizationsQuery.data])
  const organizationsById = useMemo(() => new Map(organizations.map((organization) => [organization.id, organization])), [organizations])
  const usersWithOrganization = useMemo(() => getUsersWithOrganization(usersQuery.data ?? []), [usersQuery.data])
  const rows = useMemo(() => filterUsersByEmail(usersWithOrganization, search), [usersWithOrganization, search])

  const organizationText = useCallback(
    (user: InternalUser, byId: Map<string, InternalOrganization>) => {
      const organization = user.organization ? byId.get(user.organization) : undefined
      return organization ? getOrganizationLabel(organization) : t('internal-csm:users_unknown_organization')
    },
    [t]
  )

  const tableColumns: GRTableColumn<InternalUser, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-csm:edit_user_first_name'),
        accessor: ({ row }) => nameOrDash(row.firstName),
        sortable: true,
        sortAccessor: ({ row }) => (row.firstName ?? '').toLowerCase(),
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left', sx: { overflowWrap: 'anywhere' } },
        headerCellProps: { align: 'left' },
      },
      {
        labelAccessor: t('internal-csm:edit_user_last_name'),
        accessor: ({ row }) => nameOrDash(row.lastName),
        sortable: true,
        sortAccessor: ({ row }) => (row.lastName ?? '').toLowerCase(),
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left', sx: { overflowWrap: 'anywhere' } },
        headerCellProps: { align: 'left' },
      },
      {
        labelAccessor: t('internal-csm:edit_user_email'),
        accessor: ({ row }) => row.email,
        sortable: true,
        sortAccessor: ({ row }) => row.email.toLowerCase(),
        sortOrder: SortOrder.DESC,
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left', sx: { overflowWrap: 'anywhere' } },
        headerCellProps: { align: 'left', sx: { minWidth: 240 } },
      },
      {
        labelAccessor: t('internal-csm:users_organization'),
        accessor: ({ row, customTableProps }) => {
          const organization = row.organization ? customTableProps?.organizationsById.get(row.organization) : undefined
          if (!organization) {
            return (
              <Typography variant="body2" color="text.secondary">
                {t('internal-csm:users_unknown_organization')}
              </Typography>
            )
          }
          return (
            <Link
              component={RouterLink}
              to={`/internal/organizations?users=${organization.id}`}
              variant="body2"
              color={organization.name ? undefined : 'text.secondary'}
              title={t('internal-csm:users_organization_users_open')}
            >
              {getOrganizationLabel(organization)}
            </Link>
          )
        },
        sortable: true,
        sortAccessor: ({ row, customTableProps }) => (customTableProps ? organizationText(row, customTableProps.organizationsById).toLowerCase() : ''),
        defaultSortOrder: SortOrder.DESC,
        cellProps: { align: 'left', sx: { overflowWrap: 'anywhere' } },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-csm:organization_users_status'),
        accessor: ({ row }) => <UserStatusIcon user={row} />,
        sortable: true,
        sortAccessor: ({ row }) => userStatusOrder[getUserStatus(row)],
        headerCellProps: { sx: { minWidth: 72 } },
      },
      {
        labelAccessor: '',
        accessor: ({ row, customTableProps }) => {
          const known = !!row.organization && !!customTableProps?.organizationsById.has(row.organization)
          const toggleLabel = t(row.active ? 'internal-csm:organization_users_disable' : 'internal-csm:organization_users_enable')
          return (
            <RowActions
              actions={[
                {
                  type: 'edit',
                  label: known ? t('internal-csm:organization_users_edit') : t('internal-csm:users_edit_unknown_organization'),
                  ariaLabel: t('internal-csm:organization_users_edit'),
                  size: 'small',
                  disabled: !known,
                  onClick: () => customTableProps?.onEdit(row),
                },
                { icon: <DriveFileMove />, label: t('internal-csm:users_move'), size: 'small', onClick: () => customTableProps?.onMove(row) },
                { icon: row.active ? <PersonOff /> : <Restore />, label: toggleLabel, size: 'small', onClick: () => customTableProps?.onToggleActive(row) },
                { type: 'delete', label: t('internal-csm:organization_users_remove'), size: 'small', onClick: () => customTableProps?.onRemove(row) },
              ]}
            />
          )
        },
        cellProps: { align: 'right', sx: { px: 1 } },
        headerCellProps: { sx: { minWidth: 152 } },
      },
    ],
    [t, organizationText]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(() => {
    const organizationOf = (user: InternalUser) => (user.organization ? organizationsById.get(user.organization) : undefined)
    return {
      organizationsById,
      onEdit: (user) => setEditing({ open: true, user, organization: organizationOf(user) }),
      onMove: (user) => setMoving({ open: true, user }),
      onToggleActive: (user) =>
        setConfirm({
          title: t(user.active ? 'internal-csm:organization_users_disable' : 'internal-csm:organization_users_enable'),
          confirmText: t(user.active ? 'internal-csm:organization_users_disable_confirm' : 'internal-csm:organization_users_enable_confirm', {
            name: getUserDisplayName(user),
          }),
          actionText: t(user.active ? 'internal-csm:organization_users_disable_action' : 'internal-csm:organization_users_enable_action'),
          destructiveAction: user.active,
          data: { kind: 'toggle', user },
        }),
      onRemove: (user) =>
        setConfirm({
          title: t('internal-csm:organization_users_remove'),
          confirmText: t('internal-csm:organization_users_remove_confirm', {
            name: getUserDisplayName(user),
            organization: organizationText(user, organizationsById),
          }),
          actionText: t('internal-csm:organization_users_remove_action'),
          destructiveAction: true,
          data: { kind: 'remove', user },
        }),
    }
  }, [organizationsById, organizationText, t])

  const failure = (key: string, error: unknown) => {
    const reason = getValidationErrorMessage(error)
    notify(reason ? `${t(key)}: ${reason}` : t(key), 'error')
  }
  const failureMessages = (key: string) => ({ error: t(key), errorWithReason: (reason: string) => `${t(key)}: ${reason}` })

  const handleConfirm = async (confirmed?: ConfirmDialogData<PendingAction>) => {
    setConfirm(undefined)
    const action = confirmed?.data
    if (!action?.user.organization) return
    const { user } = action
    const organizationId = user.organization as string
    if (action.kind === 'toggle') {
      const active = !user.active
      try {
        const { stored } = await setActive({ organizationId, username: user.username, active }).unwrap()
        if (stored?.active !== active) notify(t('internal-csm:organization_users_status_not_stored', { email: user.email }), 'error')
        else notify(t('internal-csm:organization_users_status_changed', { email: user.email }), 'success')
      } catch (error) {
        if (isFetchBaseQueryError(error) && error.status === 409) notify(t('internal-csm:organization_users_no_seats'), 'error')
        else failure('internal-csm:organization_users_status_failed', error)
      }
      return
    }
    const removed = await run(
      removeUser({ organizationId, username: user.username }).unwrap(),
      failureMessages('internal-csm:organization_users_remove_failed')
    )
    if (!removed.ok) return
    if (removed.result.stored) notify(t('internal-csm:organization_users_not_removed', { email: user.email }), 'error')
    else notify(t('internal-csm:organization_users_removed', { email: user.email, organization: organizationText(user, organizationsById) }), 'success')
  }

  const isLoading = usersQuery.isLoading || organizationsQuery.isLoading
  const isError = usersQuery.isError || organizationsQuery.isError
  const orphanCount = orphansQuery.data?.length

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
        <TextField
          size="small"
          type="search"
          label={t('internal-csm:users_search')}
          value={search}
          onChange={(event) => setParam('search', event.target.value)}
          sx={{ minWidth: 320 }}
        />
        {!isLoading && !isError && (
          <Typography variant="body2" color="text.secondary">
            {t('internal-csm:users_count', { count: rows.length, total: usersWithOrganization.length })}
          </Typography>
        )}
      </Stack>
      <Typography variant="body2" mb={2}>
        {t('internal-csm:organizations_orphaned_users')}{' '}
        {orphansQuery.isLoading ? (
          <Skeleton component="span" width={24} sx={{ display: 'inline-block' }} />
        ) : orphansQuery.isError ? (
          <Typography component="span" variant="body2" color="error.main">
            {t('internal-csm:organizations_orphaned_users_failed')}
          </Typography>
        ) : orphanCount ? (
          <Link component="button" variant="body2" onClick={() => setParam('orphans', '1')} aria-label={t('internal-csm:organizations_orphaned_users_open')}>
            {orphanCount}
          </Link>
        ) : (
          0
        )}
      </Typography>

      <QueryStateBoundary isLoading={isLoading} isError={isError} errorText={t('internal-csm:users_load_failed')} skeletonHeight={480}>
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      {editing.organization && (
        <EditUserDialog
          open={editing.open}
          organization={editing.organization}
          user={editing.user}
          onClose={() => setEditing((current) => ({ ...current, open: false }))}
        />
      )}
      <MoveUserDialog
        open={moving.open}
        user={moving.user}
        organizations={organizations}
        onClose={() => setMoving((current) => ({ ...current, open: false }))}
      />
      <ConfirmDialog open={!!confirm} confirmDialogData={confirm} onClose={handleConfirm} />
      <OrphanedUsersDialog open={orphansOpen} onClose={() => setParam('orphans', null)} />
    </Box>
  )
}
